<template>
  <billing-dialog
    class="opc-zp-aic-detail-dialog"
    v-model="visible"
    @close="handleCancel"
    @back-event="handleCancel"
    show-close
  >
    <div class="opc-zp-aic-detail">
      <h2 class="opc-zp-aic-detail__title">{{ displayTitle }}</h2>

      <div v-if="isFreeTrial" class="opc-zp-aic-detail__badge">
        {{ $t('billing.pbx.zp_aic.plan_details.badge_free_trial', { days: trialDaysForBadge }) }}
      </div>

      <div class="opc-zp-aic-detail__content">
        <!-- Trial: single PAYG pricing -->
        <div v-if="isFreeTrial" class="opc-zp-aic-detail__pricing">
          <p class="opc-zp-aic-detail__tier-label">{{ $t('billing.pay_you_go') }}</p>
          <div class="opc-zp-aic-detail__price-row">
            <span class="opc-zp-aic-detail__amount">{{ $money(0) }}</span>
            <span class="opc-zp-aic-detail__unit" v-html="$t('billing.pbx.zp_aic.then_billed_monthly', { price: unitPriceText })"></span>
          </div>
        </div>

        <!-- Paid: plan tiers in 2-column rows with vertical dividers -->
        <template v-if="!isFreeTrial && planRows.length">
          <div v-for="(row, ri) in planRows" :key="ri" class="opc-zp-aic-detail__tier-row">
            <template v-for="(plan, ci) in row" :key="plan.plan_code">
              <div v-if="ci > 0" class="opc-zp-aic-detail__tier-divider"></div>
              <div class="opc-zp-aic-detail__tier-cell">
                <p v-if="plan.is_payg" class="opc-zp-aic-detail__tier-label">{{ $t('billing.pay_you_go') }}</p>
                <p v-else class="opc-zp-aic-detail__tier-label">
                  <span>{{ $t('billing.prepaid') }}</span>
                  <span class="opc-zp-aic-detail__tier-mins">{{ formatMins(plan.minutes) }}</span>
                </p>
                <div class="opc-zp-aic-detail__price-row">
                  <span class="opc-zp-aic-detail__amount">{{ $money(plan.amount) }}</span>
                  <span class="opc-zp-aic-detail__unit" v-html="getPlanUnitHtml(plan)"></span>
                </div>
              </div>
            </template>
          </div>
        </template>

        <ul class="opc-zp-aic-detail__features">
          <li v-for="(feat, idx) in featureKeys" :key="idx" class="opc-zp-aic-detail__feature">
            <img src="@/assets/check_blue.svg" alt="" class="opc-zp-aic-detail__check" />
            <p>
              <span class="opc-zp-aic-detail__feature-title">{{ $t(feat.title) }}</span>
              – {{ $t(feat.desc) }}
            </p>
          </li>
        </ul>
      </div>
    </div>
  </billing-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import { ConfirmComponent } from '@/components/common/service-dialog/confirm';
import BillingDialog from '@/components/billing-dialog.vue';
import { planTypes } from '@/common/constants';
import { planUnitMap } from '@/common/product-unit';
import {
  PLAN_DISPLAY_NAME,
  PLAN_DISPLAY_NAME_FREE_TRIAL,
  PLAN_DETAIL_FEATURE_KEYS,
} from '@/plans/ZOOM_PHONE$AICONCIERGE/constants';

export default {
  name: 'ZpAicPlanDetailsDialog',
  mixins: [ConfirmComponent],
  components: { BillingDialog },
  props: {
    title: { type: String, default: '' },
    trackingPlanName: { type: String, default: '' },
    /** Trial: "$0.30/min" text for "then billed" line */
    unitPriceText: { type: String, default: '' },
    /** Paid: all plan objects sorted */
    plans: { type: Array, default: () => [] },
  },
  data() {
    return {
      visible: false,
      featureKeys: PLAN_DETAIL_FEATURE_KEYS,
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans']),
    selectedAicPlan() {
      return this.getSelectedPlans.find((p) => p.type === planTypes.ZOOM_PHONE$AICONCIERGE) || null;
    },
    isFreeTrial() {
      return !!this.selectedAicPlan?.isOnlineFreeTrial;
    },
    trialDaysForBadge() {
      return this.selectedAicPlan?.trialDays;
    },
    displayTitle() {
      return this.isFreeTrial
        ? PLAN_DISPLAY_NAME_FREE_TRIAL
        : (this.title || PLAN_DISPLAY_NAME);
    },
    planRows() {
      const cols = 2;
      const rows = [];
      for (let i = 0; i < this.plans.length; i += cols) {
        rows.push(this.plans.slice(i, i + cols));
      }
      return rows;
    },
  },
  watch: {
    visible(val) {
      this.$tracking.buyInteraction('plan details selected', `${this.trackingPlanName} plan details - ${val ? 'impression' : 'exit'}`);
    },
  },
  methods: {
    formatMins(mins) {
      if (!mins) return '';
      return `${new Intl.NumberFormat().format(mins)} ${this.$t('billing.pbx.zp_aic.mins_abbr')}`;
    },
    getPlanUnitHtml(plan) {
      if (plan?.unit !== planUnitMap.perMinute) {
        return plan?.billing_cycle ? this.$t(`billing.common.per_billing_cycle.${plan.billing_cycle}`) : '';
      }
      if (plan?.is_payg) {
        return this.$t('billing.pbx.zp_aic.plan_details.per_min_billed_monthly');
      }
      const totalAmount = plan?.totalAmount || (plan?.amount || 0) * (plan?.minutes || 0);
      return this.$t('billing.pbx.zp_aic.plan_details.per_min_total_billed_monthly', { total: this.$money(totalAmount) });
    },
  },
};
</script>

<style lang="scss">
.opc-zp-aic-detail-dialog {
  &.zbo-dialog {
    padding: 72px 60px;

    .zbo-dialog__header {
      padding: 0;
    }

    .zbo-dialog__body {
      padding: 0;
      margin: 0;
    }
  }

  &.opc-modal-dialog .opc-zp-aic-detail {
    padding: 20px;
  }
}

.opc-zp-aic-detail {
  &__title {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.5;
    color: #00053d;
    margin: 0 0 4px;
  }

  &__badge {
    display: inline-block;
    padding: 0 6px;
    border-radius: 100px;
    background: var(--Saving, linear-gradient(93deg, #268543 7.27%, #1C837E 92.73%));
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #fff;
    margin-bottom: 4px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__pricing {
    margin: 0;
  }

  &__tier-row {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  &__tier-cell {
    width: 260px;
    flex-shrink: 0;
  }

  &__tier-divider {
    width: 1px;
    flex-shrink: 0;
    align-self: stretch;
    background: var(--zoom-color-border-neutral-moderate, #b8b8c4);
  }

  &__tier-label {
    display: flex;
    gap: 4px;
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: var(--zoom-color-text-stronger-neutral, #2a2b2d);
    margin: 0;
  }

  &__tier-mins {
    color: var(--zoom-color-text-neutral, #686f79);
  }

  &__price-row {
    display: flex;
    align-items: baseline;
    gap: 2px;
    flex-wrap: wrap;
  }

  &__amount {
    font-size: 24px;
    font-weight: 700;
    line-height: 28px;
    color: #00053d;
  }

  &__unit {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: #00053d;

    b {
      font-weight: 600;
    }
  }

  &__features {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__feature {
    display: flex;
    gap: 4px;
    align-items: flex-start;

    p {
      margin: 0;
      font-size: 14px;
      line-height: 1.5;
      color: #00053d;
      flex: 1;
    }
  }

  &__feature-title {
    font-weight: 600;
  }

  &__check {
    width: 16px;
    height: 21px;
    flex-shrink: 0;
    object-fit: none;
  }
}
</style>
